import React, { useState } from 'react';
import { ReusableModal } from '../modals/ReusableModal';
import { useLocalStorage } from '../helpers/useLocalStorage';
import { numberFormatter } from '../helpers/formatters';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import { useGetNoticeRecordHistoryQuery } from '../services/edgeApi';
import { getContrastColor } from '../utils/colors';
import { NoticeRecord, NoticeRecordListItem } from '@/types/api/NoticeRecord';

type NoticeHistoryOption = keyof NoticeRecordListItem | 'display_text';

export type NoticeRecordHistoryProps = {
  componentId?: string | number;
  setHistoryModal: (isOpen: boolean) => void;
  getFlowsOnDate: (gasDate: string, nominationCycle: string) => void;
};

export const NoticeRecordHistory: React.FC<NoticeRecordHistoryProps> = ({
  componentId,
  setHistoryModal,
  getFlowsOnDate,
}) => {
  const [option, setOption] = useLocalStorage<NoticeHistoryOption>(
    'notice_history',
    'display_text',
  );
  const [rawNoticeModal, setRawNoticeModal] = useState(false);
  const [filePath, setFilePath] = useState('');

  const {
    data: records = [],
    isLoading,
    isFetching,
  } = useGetNoticeRecordHistoryQuery(
    { componentId: componentId! },
    { skip: !componentId },
  );

  //This option list is used to control which variable you want to see in history modal
  const optionList: {
    value: NoticeHistoryOption;
    title: string;
    show: boolean;
  }[] = [
    { value: 'display_text', title: 'Summary', show: true },
    { value: 'percentage', title: 'Percent Constrained', show: true },
    { value: 'priority', title: 'Priority', show: true },
    { value: 'capacity', title: 'OAC', show: true },
    { value: 'identifier', title: 'Identifier', show: false },
    { value: 'identifier_type', title: 'Identifier Type', show: false },
    { value: 'id', title: 'ID', show: true },
  ];

  const gasDates = [
    ...new Set(records.map((record) => record.start_gas_date)),
  ].sort((a, b) => {
    return a > b ? 1 : -1;
  });

  const generateDateRange = (startDate: string, endingDate: string) => {
    const dates = [];
    let currentDate = new Date(startDate);
    currentDate.setDate(currentDate.getDate() + 1);
    let endDate = new Date(endingDate);
    endDate.setDate(endDate.getDate() + 1);

    while (currentDate <= endDate) {
      dates.push(getYYYYMMDD(new Date(currentDate)));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  };

  const getYYYYMMDD = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const updatedTime = generateDateRange(
    gasDates[0],
    gasDates[gasDates.length - 1],
  );

  // we can either create nomCycle columns based on data, or hard code columns - either way we should force all nom cycles to be consistent name and order in the backend
  const nomCycles = ['T', 'E', 'I1', 'I2', 'I3'];
  // what's the best way to format outputs?  Would be great to do it all in one place.  I tried in the backend serializers, but it caused a problem because turning the numbers into strings means math doesn't work.  Maybe we should send a formatted and non formatted value from backend?
  // should put this function somewhere to be called from other components
  const formatConcept = (
    concept: NoticeHistoryOption,
    record: NoticeRecord,
    sign = 1,
  ) => {
    if (concept === 'display_text') {
      return record.display_values?.text || record.identifier;
    }

    const value = record[concept];
    if (['capacity_units', 'capacity'].includes(concept)) {
      return numberFormatter(sign * Number(value));
    } else if (concept === 'percentage')
      return `${record?.priority}: ${value}%`;
    return `${value}`;
  };

  const RawNoticeModal = () => {
    const link =
      'https://pathpoint-space.nyc3.digitaloceanspaces.com/' + filePath;
    return rawNoticeModal ? (
      <ReusableModal
        size="lg"
        isOpen={rawNoticeModal}
        toggle={() => setRawNoticeModal(false)}
        header="raw notice data"
      >
        {filePath.substring(filePath.length - 4) === '.pdf' ||
        filePath.substring(filePath.length - 5) === '.html' ? (
          <iframe
            title="pdf"
            src={link}
            width="740"
            height="480"
            allow="autoplay"
          ></iframe>
        ) : (
          'No raw data found.'
        )}
      </ReusableModal>
    ) : (
      ''
    );
  };

  return (
    <>
      <ReusableModal
        size="lg"
        isOpen={true}
        toggle={() => setHistoryModal(false)}
        header={'History'}
      >
        {!records.length ? (
          !isLoading && !isFetching && <span>No Notice History Found</span>
        ) : (
          <>
            <div className="input-group my-3">
              <label
                className="input-group-text"
                htmlFor="notice-history-variable-select"
              >
                Variable
              </label>
              <select
                id="notice-history-variable-select"
                className="form-select"
                value={option}
                onChange={(e) => {
                  setOption(e.target.value as NoticeHistoryOption);
                }}
              >
                {optionList
                  .filter((item) => item.show)
                  .map((item) => {
                    return (
                      <option value={item.value} key={item.value}>
                        {item.title}
                      </option>
                    );
                  })}
              </select>
            </div>
            <table
              className="table table-striped mt-3"
              style={{ textAlign: 'center' }}
            >
              <thead>
                <tr>
                  <th style={{ width: '150px' }}>Gas Date</th>
                  {nomCycles.map((nomCycle) => (
                    <th key={nomCycle}>{nomCycle}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {updatedTime
                  .sort((a, b) => (a > b ? -1 : 1))
                  .map((gasDate) => (
                    <tr key={gasDate}>
                      <th>{new Date(gasDate).toLocaleDateString()}</th>
                      {nomCycles.map((nomCycle) => {
                        const record = records.find((record) => {
                          if (
                            record.nomination_cycle === null ||
                            record.nomination_cycle === 'None'
                          )
                            return record.start_gas_date === gasDate;
                          return (
                            record.start_gas_date === gasDate &&
                            record.nomination_cycle === nomCycle
                          );
                        });
                        const hasValue =
                          option === 'display_text'
                            ? Boolean(record?.display_values?.text || record?.identifier)
                            : Boolean(record?.[option]);
                        const backgroundColor = hasValue
                          ? '#1B6596'
                          : 'transparent';
                        return (
                          <>
                            <td
                              key={`${gasDate}-${nomCycle}`}
                              style={{
                                backgroundColor,
                                color: getContrastColor(backgroundColor),
                              }}
                              onClick={() => getFlowsOnDate(gasDate, nomCycle)}
                            >
                              {/* see platts_pipeline_flow.py about whether we want to set backhaul flow records negative in backend, or just show them as negative in front end */}
                              {/* doing it in the frontend for now as a test to see whether negative flows are intuitive */}
                              <div
                                title={
                                  hasValue
                                    ? 'click to see raw data'
                                    : ''
                                }
                                style={
                                  hasValue ? { cursor: 'pointer' } : {}
                                }
                                onClick={() => {
                                  if (hasValue && record) {
                                    setRawNoticeModal(true);
                                    setFilePath(record?.notice_file_path);
                                  }
                                }}
                              >
                                {hasValue && record
                                  ? formatConcept(option, record)
                                  : ' - '}
                              </div>
                            </td>
                          </>
                        );
                      })}
                    </tr>
                  ))}
              </tbody>
            </table>
          </>
        )}
        {(isFetching || isLoading) && <LoadingIndicator />}
      </ReusableModal>
      <RawNoticeModal></RawNoticeModal>
    </>
  );
};
